import React, { useState } from 'react';
import classnames from 'classnames';
import { Dropdown } from 'react-bootstrap';
import { DjangoListResponse, User } from 'v2/models';
import { InfiniteData, useMutation, useQueryClient } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { toast } from 'react-toastify';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import styles from './memberActions.module.scss';
import { updateInfiniteData } from 'v2/utils/optimisticUpdate';
import ReviewLicensesModal from '../ModalReviewLicenses/ReviewLicensesModal';
import ReviewPermissionsModal from '../ModalReviewPermissions/ReviewPermissionsModal';
import { infiniteStrapiUsersKeys } from 'v2/queries';
import { VerticalDotsIcon } from 'v2/icons';

type MemberActionsProps = {
  isDisabled: boolean;
  member: User;
};

type MemberMutationProps = {
  id: number;
  action: 'activate' | 'deactivate';
};

const MemberActions = ({ isDisabled, member }: MemberActionsProps) => {
  const queryClient = useQueryClient();
  const [memberToDeactivate, setMemberToDeactivate] = useState<User | null>(
    null
  );
  const [showReviewLicenses, setShowReviewLicenses] = useState(false);
  const [showReviewPermissions, setShowReviewPermissions] = useState(false);

  const { mutate: mutateMember, isLoading: isMutatingMember } = useMutation(
    ({ id, action }: MemberMutationProps) =>
      restFactory.patch<User>(`/dj/api/v1/strapi_user/${id}/${action}/`),
    {
      onSuccess: (member) => {
        queryClient.setQueryData(
          infiniteStrapiUsersKeys.all,
          (teamData?: InfiniteData<DjangoListResponse<User>>) =>
            updateInfiniteData(teamData, member)
        );

        if (memberToDeactivate) setMemberToDeactivate(null);
        toast.success('Team member updated.');
      },
      onError: () => {
        queryClient.invalidateQueries(infiniteStrapiUsersKeys.all);
        toast.error('Failed to update team member.');
      },
    }
  );

  const handleCloseReviewLicensesModal = () => {
    setShowReviewLicenses(false);
  };

  const handleCloseReviewPermissionsModal = () => {
    setShowReviewPermissions(false);
  };

  return (
    <>
      <Dropdown className={classnames(styles.dropdown)}>
        <Dropdown.Toggle
          variant="link"
          className={classnames('p-0')}
          data-cy="team-member-actions-dropdown"
        >
          <VerticalDotsIcon
            style={{
              width: 20,
              height: 20,
            }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          <Dropdown.Item
            disabled={isDisabled}
            onClick={() => setShowReviewLicenses(true)}
          >
            Licenses
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            disabled={isDisabled}
            onClick={() => setShowReviewPermissions(true)}
          >
            Permissions
          </Dropdown.Item>

          <Dropdown.Divider />

          {member.active ? (
            <Dropdown.Item onClick={() => setMemberToDeactivate(member)}>
              Deactivate
            </Dropdown.Item>
          ) : (
            <Dropdown.Item
              onClick={() =>
                mutateMember({
                  id: member.id,
                  action: 'activate',
                })
              }
            >
              Reactivate
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <ConfirmationModal
        show={memberToDeactivate !== null}
        onCancel={() => setMemberToDeactivate(null)}
        onOk={() => {
          if (memberToDeactivate) {
            mutateMember({
              id: memberToDeactivate.id,
              action: 'deactivate',
            });
          }
        }}
        okBtnText="Yes"
        cancelBtnText="No"
        title="Disable team member"
        isLoading={isMutatingMember}
      >
        <p>
          Disabling a user will prevent them from logging in and will remove all
          their licenses. Are you sure? Yes/No
        </p>
      </ConfirmationModal>
      {showReviewLicenses && (
        <ReviewLicensesModal
          onClose={handleCloseReviewLicensesModal}
          member={member}
        />
      )}
      {showReviewPermissions && (
        <ReviewPermissionsModal
          onClose={handleCloseReviewPermissionsModal}
          member={member}
        />
      )}
    </>
  );
};

export default MemberActions;
